import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';

import { deleteHistoryJob, listHistoryJobs, rerunJob } from '../api';
import { calculateAdsJobCost } from '../hooks/useAdsStats';
import type { DateRange, Job } from '../types';
import { AdsImagesModal } from './AdsImagesModal';

/**
 * History table for completed/failed ads jobs.
 *
 * Inspired by `web/src/interior/components/HistoricalTable.tsx` but
 * trimmed down for the ads UX:
 *  - clicking a completed row opens the {@link AdsImagesModal}
 *    rendering every produced format with download buttons.
 *  - the actions menu has Rerun + Delete.
 *  - cost is the FAL + Gemini `direct_cost` sum (per
 *    :func:`web/src/ads/hooks/useAdsStats.calculateAdsJobCost`).
 */

const PAGE_SIZE = 25;

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs.toFixed(1)}s`;
}

function formatCost(cost: number | null | undefined): string {
  if (cost == null || cost === 0) return '-';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  const base =
    'px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5';
  if (status === 'completed') {
    return (
      <span className={`${base} status-completed`}>
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        <span className="status-text">Completed</span>
      </span>
    );
  }
  return (
    <span className={`${base} status-failed`}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
      <span className="status-text">Failed</span>
    </span>
  );
}

// Inline UVCamp link cell. Mirrors the equivalent helper in
// `LiveScansTable` so the History table renders a consistent "Link"
// button per row. Clicks are intentionally stopped from bubbling so
// the parent row's open-modal handler doesn't fire when an operator
// just wanted to jump to UVCamp.
function UVCampLink({ link }: { link: string | null }) {
  if (!link) return <span className="text-slate-400">-</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(link, '_blank', 'noopener,noreferrer');
      }}
      className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium cursor-pointer bg-transparent border-none p-0"
    >
      Link
    </button>
  );
}

function formatsCount(job: Job): number {
  if (!job.result || !Array.isArray(job.result.formats)) return 0;
  return job.result.formats.length;
}

export function HistoricalTable() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [scanIdFilter, setScanIdFilter] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({ from: null, to: null });

  const [openModal, setOpenModal] = useState<{ jobId: string; scanId: string } | null>(null);
  const [rerunning, setRerunning] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  // Mobile cards collapse to Scan ID / Status / Cost by default; tapping
  // a card expands it (rather than opening the modal). Keyed by job_id.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedRows(new Set());
  }, [jobs]);

  const toggleRow = useCallback((jobId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }, []);

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / PAGE_SIZE) : 1),
    [total],
  );

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listHistoryJobs(
        dateRange,
        statusFilter,
        page,
        PAGE_SIZE,
        scanIdFilter || null,
      );
      setJobs(resp.jobs);
      setTotal(resp.total);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Failed to load history');
      setJobs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [dateRange, statusFilter, page, scanIdFilter]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleRerun = useCallback(async (jobId: string) => {
    setRerunning(jobId);
    try {
      await rerunJob(jobId);
      alert('Job resubmitted');
      await fetchPage();
    } catch (ex) {
      alert(ex instanceof Error ? ex.message : 'Failed to rerun');
    } finally {
      setRerunning(null);
    }
  }, [fetchPage]);

  const handleDelete = useCallback(async (jobId: string) => {
    if (!confirm('Delete this ads job and its outputs?')) return;
    setDeleting(jobId);
    try {
      await deleteHistoryJob(jobId);
      await fetchPage();
    } catch (ex) {
      alert(ex instanceof Error ? ex.message : 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  }, [fetchPage]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden">
      {openModal && (
        <AdsImagesModal
          jobId={openModal.jobId}
          scanId={openModal.scanId}
          onClose={() => setOpenModal(null)}
        />
      )}

      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-slate-900">Ads History</h2>

        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <input
            type="text"
            placeholder="Filter by scan ID…"
            value={scanIdFilter}
            onChange={(e) => {
              setPage(1);
              setScanIdFilter(e.target.value);
            }}
            className="px-3 py-1 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />

          <select
            value={statusFilter ?? ''}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value || null);
            }}
            className="px-2 py-1 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          <input
            type="date"
            value={dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              setPage(1);
              setDateRange((prev) => ({
                ...prev,
                from: e.target.value ? new Date(e.target.value) : null,
              }));
            }}
            className="px-2 py-1 text-xs border border-slate-200 rounded-md"
          />
          <span className="text-slate-400 text-xs">to</span>
          <input
            type="date"
            value={dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              setPage(1);
              setDateRange((prev) => ({
                ...prev,
                to: e.target.value ? new Date(e.target.value + 'T23:59:59') : null,
              }));
            }}
            className="px-2 py-1 text-xs border border-slate-200 rounded-md"
          />

          <button
            onClick={fetchPage}
            className="btn-secondary px-2.5 py-1 rounded-md text-xs"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="px-5 py-2 bg-red-50 border-b border-red-100 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-slate-500">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="p-12 text-center text-slate-500">
          <p className="font-medium text-slate-900">No ads jobs in this range</p>
          <p className="text-sm">Try expanding the date range or removing filters.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table mobile-collapsible summary-inline">
            <thead>
              <tr>
                <th className="px-5 py-3 text-left">Scan ID</th>
                <th className="px-5 py-3 text-left">UVCamp</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Source</th>
                <th className="px-5 py-3 text-left">Formats</th>
                <th className="px-5 py-3 text-left">Engine</th>
                <th className="px-5 py-3 text-left">Duration</th>
                <th className="px-5 py-3 text-left">Cost</th>
                <th className="px-5 py-3 text-left">Completed</th>
                <th className="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((job) => {
                const engine =
                  (job.result?.engine as string | undefined) ?? '-';
                const completed = job.completed_at
                  ? format(new Date(job.completed_at), 'MMM d, HH:mm')
                  : '-';
                const cost = calculateAdsJobCost(job);
                return (
                  <tr
                    key={job.job_id}
                    data-collapsed={!expandedRows.has(job.job_id)}
                    className={`hover:bg-slate-50 transition-colors ${
                      job.status === 'completed' ? 'cursor-pointer' : ''
                    }`}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, a, input, select')) return;
                      // On mobile the card is collapsible: tap toggles
                      // detail instead of opening the modal.
                      if (window.matchMedia('(max-width: 767px)').matches) {
                        toggleRow(job.job_id);
                        return;
                      }
                      if (job.status === 'completed') {
                        setOpenModal({ jobId: job.job_id, scanId: job.scan_id });
                      }
                    }}
                  >
                    <td className="px-5 py-3" data-label="Scan ID">
                      <span
                        className="font-mono text-xs text-indigo-600 font-medium whitespace-nowrap"
                        title={job.scan_id}
                      >
                        <span className="scan-id-short">{job.scan_id?.substring(0, 8)}</span>
                        <span className="scan-id-full">{job.scan_id}</span>
                      </span>
                    </td>
                    <td className="px-5 py-3" data-label="UVCamp">
                      <UVCampLink link={job.uvcamp_link} />
                    </td>
                    <td className="px-5 py-3" data-label="Status">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600" data-label="Source">
                      {job.source ?? '-'}
                    </td>
                    <td className="px-5 py-3 text-center text-slate-600" data-label="Formats">
                      {formatsCount(job)}
                    </td>
                    <td className="px-5 py-3 text-xs font-mono text-slate-600" data-label="Engine">
                      {engine}
                    </td>
                    <td className="px-5 py-3 text-slate-600" data-label="Duration">
                      {formatDuration(job.duration_seconds)}
                    </td>
                    <td className="px-5 py-3 text-slate-600" data-label="Cost">{formatCost(cost)}</td>
                    <td className="px-5 py-3 text-xs text-slate-500" data-label="Completed">{completed}</td>
                    <td className="px-5 py-3" data-label="Actions">
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {job.status === 'completed' && (
                          <button
                            onClick={() =>
                              setOpenModal({ jobId: job.job_id, scanId: job.scan_id })
                            }
                            className="px-2 py-1 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md"
                          >
                            View
                          </button>
                        )}
                        <button
                          onClick={() => handleRerun(job.job_id)}
                          disabled={rerunning === job.job_id}
                          className="px-2 py-1 text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md disabled:opacity-50"
                        >
                          {rerunning === job.job_id ? 'Rerunning…' : 'Rerun'}
                        </button>
                        <button
                          onClick={() => handleDelete(job.job_id)}
                          disabled={deleting === job.job_id}
                          className="px-2 py-1 text-xs text-[#F0544F] bg-[#F0544F]/10 hover:bg-[#F0544F]/20 rounded-md disabled:opacity-50"
                        >
                          {deleting === job.job_id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
        <span>
          {total > 0
            ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(
                page * PAGE_SIZE,
                total,
              )} of ${total}`
            : 'No results'}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="btn-secondary px-2 py-1 text-xs disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="btn-secondary px-2 py-1 text-xs disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
